/** @file Creates a region attachment on an image (sized to the image); skin.setEntry maps it to a slot. */
import { z } from 'zod';
import { prim } from '@pose2d/format/doc/schemaParts/primitives';
import { defineCommand } from '../defineCommand';
import { defineCreate } from '../_shared/defineCreate';
import { createShape } from './createShape';

const REGION_DEFAULTS = { color: '#ffffffff', x: 0, y: 0, rotation: 0, scaleX: 1, scaleY: 1, width: 1, height: 1 };

const create = defineCreate({
  domain: 'attachment',
  kind: 'attachment',
  description: 'Create a region attachment showing `image` (name or id) in its slot bone space: x, y, rotation, scaleX, scaleY (identity by default), width/height (default: the image size), color. It is not bound to a slot: map it with skin.setEntry and show it with slot.setAttachment.',
  schema: z
    .object({
      ...createShape,
      x: prim.finite.exactOptional(),
      y: prim.finite.exactOptional(),
      rotation: prim.finite.exactOptional(),
      scaleX: prim.finite.exactOptional(),
      scaleY: prim.finite.exactOptional(),
      width: prim.finite.positive().exactOptional(),
      height: prim.finite.positive().exactOptional(),
    })
    .strict(),
  build(input, ctx) {
    const r = { ...REGION_DEFAULTS, ...input };
    return { type: 'region', name: r.name, image: ctx.resolve('image', r.image), color: r.color, x: r.x, y: r.y, rotation: r.rotation, scaleX: r.scaleX, scaleY: r.scaleY, width: r.width, height: r.height };
  },
});

/** attachment.createRegion: the size defaults are read from the image before the factory builds the record. */
export const command = defineCommand({
  ...create,
  name: 'attachment.createRegion',
  apply(draft, input, ctx) {
    const image = draft.images[ctx.resolve('image', input.image)];
    create.apply(draft, { width: image?.w ?? 1, height: image?.h ?? 1, ...input }, ctx);
  },
});
